/**
 * Game Screen Component
 * Main game board with HUD, play zone, and player hand
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Card } from '../../common/Card';
import type { Card as CardType, PlaySlot, Suit } from '../../../types/game';
import styles from './GameScreen.module.css';

interface GameScreenProps {
  // Game state
  playerHand: CardType[];
  botHandCount: number;
  playZone: PlaySlot[];
  deckCount: number;
  discardCount: number;
  trump: Suit;
  
  // Progress
  xp: number;
  streak: number;
  maxXp?: number;
  
  // Turn state
  isPlayerAttacking: boolean;
  isBotThinking?: boolean;
  
  // Selection
  selectedCard: CardType | null;
  onCardSelect: (card: CardType) => void;
  
  // Actions
  onPass: () => void;
  onTake: () => void;
  onTracker: () => void;
  
  // Labels (i18n)
  labels: {
    botHand: string;
    yourHand: string;
    trump: string;
    deck: string;
    draw: string;
    discarded: string;
    playZone: string;
    pass: string;
    take: string;
    tracker: string;
    playArea: string;
    player: string;
    you: string;
    xpProgress: string;
    aria: {
      board: string;
      yourHandList: string;
      botHandFaceDown: string;
      playZone: string;
      deckArea: string;
      drawPile: (count: number) => string;
      discardPile: (count: number) => string;
      takeCards: string;
      passTurn: string;
      tracker: string;
      xpProgress: string;
    };
  };
}

export const GameScreen: React.FC<GameScreenProps> = ({
  playerHand,
  botHandCount,
  playZone,
  deckCount,
  discardCount,
  trump,
  xp,
  streak,
  maxXp = 500,
  isPlayerAttacking,
  isBotThinking = false,
  selectedCard,
  onCardSelect,
  onPass,
  onTake,
  onTracker,
  labels,
}) => {
  const xpPercentage = Math.min(100, (xp / maxXp) * 100);
  
  // Keyboard navigation state
  const [focusedCardIndex, setFocusedCardIndex] = React.useState<number>(-1);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent, cardIndex: number) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        const prevIndex = cardIndex > 0 ? cardIndex - 1 : playerHand.length - 1;
        setFocusedCardIndex(prevIndex);
        cardRefs.current[prevIndex]?.focus();
        break;
      case 'ArrowRight':
        e.preventDefault();
        const nextIndex = cardIndex < playerHand.length - 1 ? cardIndex + 1 : 0;
        setFocusedCardIndex(nextIndex);
        cardRefs.current[nextIndex]?.focus();
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onCardSelect(playerHand[cardIndex]);
        break;
    }
  }, [playerHand, onCardSelect]);
  
  // Reset focus when hand changes
  useEffect(() => {
    setFocusedCardIndex(-1);
    cardRefs.current = cardRefs.current.slice(0, playerHand.length);
  }, [playerHand.length]);
  
  // Calculate bot card rotations for visual fan effect
  const getBotCardStyle = (index: number, total: number): React.CSSProperties => {
    const middle = (total - 1) / 2;
    const offset = index - middle;
    return {
      transform: `rotate(${offset * 5}deg) translateX(${offset * 8}px)`,
    };
  };

  return (
    <section id="screen-game" className={`screen ${styles.screen} active`} role="main" aria-label={labels.aria.board}>
      {/* HUD */}
      <header className={styles.hud} role="banner">
        <div className={styles.hudLeft}>
          <span className={styles.hudLabel}>{labels.player}</span>
          <span style={{ fontWeight: 700 }}>{labels.you}</span>
          <span className={styles.xpText}>⚡ {xp} XP</span>
        </div>

        <div className={styles.hudCenter}>
          <span className={styles.hudLabel}>{labels.xpProgress}</span>
          <div
            className={styles.xpBarWrap}
            role="progressbar"
            aria-valuenow={Math.round(xpPercentage)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={labels.aria.xpProgress}
          >
            <div className={styles.xpBar} style={{ width: `${xpPercentage}%` }} />
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span className={styles.streakBadge}>🔥 ×{streak}</span>
          </div>
        </div>
        
        <div className={styles.hudRight}>
          <span className={styles.hudLabel}>{labels.trump}</span>
          <div className={styles.trumpIndicator}>
            <span className={styles.trumpSuit}>{trump}</span>
          </div>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
            {labels.deck}: {deckCount}
          </span>
        </div>
      </header>

      {/* Board */}
      <div className={styles.board}>
        {/* Bot hand */}
        <div className={styles.zoneLabelRow}>
          <span className={styles.zoneLabel}>{labels.botHand}</span>
          <span className={styles.countPill}>{botHandCount}</span>
        </div>
        <div className={styles.botHand} aria-label={labels.aria.botHandFaceDown}>
          {Array.from({ length: botHandCount }).map((_, i) => (
            <div
              key={i}
              className={styles.botCard}
              style={getBotCardStyle(i, botHandCount)}
            >
              <Card faceDown />
            </div>
          ))}
        </div>

        {/* Bot thinking indicator */}
        {isBotThinking && (
          <div className={styles.botThinking} aria-live="polite">
            <span>🤖</span>
            <div className={styles.botThinkingDots} aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {/* Play zone */}
        <span className={styles.zoneLabel}>{labels.playZone}</span>
        <div className={styles.playZone} aria-label={labels.aria.playZone}>
          {playZone.length === 0 ? (
            <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
              — {labels.playArea} —
            </span>
          ) : (
            playZone.map((slot, index) => (
              <div key={index} className={styles.playSlot}>
                <Card card={slot.attacker} ariaLabel={`Attack ${index + 1}`} />
                {slot.defender ? (
                  <>
                    <span className={styles.playSlotArrow}>→</span>
                    <Card card={slot.defender} ariaLabel={`Defense ${index + 1}`} />
                  </>
                ) : (
                  <>
                    <span className={styles.playSlotArrow}>→</span>
                    <div className={styles.emptySlot}>?</div>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        {/* Deck area */}
        <div className={styles.deckArea} aria-label={labels.aria.deckArea}>
          {/* Draw pile */}
          <div className={styles.pileWrapper}>
            <span className={styles.pileLabel}>{labels.draw}</span>
            <div className={styles.deckStack} role="img" aria-label={labels.aria.drawPile(deckCount)}>
              {deckCount > 0 ? (
                <>
                  <div className={styles.deckCard} style={{ top: 0, left: 0 }}><Card faceDown /></div>
                  {deckCount > 1 && <div className={styles.deckCard}><Card faceDown /></div>}
                  {deckCount > 2 && <div className={styles.deckCard}><Card faceDown /></div>}
                </>
              ) : (
                <div className={styles.emptyPile}>✕</div>
              )}
              <span className={styles.deckBadge}>{deckCount}</span>
            </div>
          </div>

          {/* Discard pile */}
          <div className={styles.pileWrapper}>
            <span className={styles.pileLabel}>{labels.discarded}</span>
            <div className={styles.discardPile} role="img" aria-label={labels.aria.discardPile(discardCount)}>
              <span className={styles.discardBadge}>{discardCount}</span>
            </div>
          </div>
        </div>

        {/* Player hand */}
        <span className={styles.zoneLabel}>{labels.yourHand}</span>
        <div className={styles.playerHand} role="list" aria-label={labels.aria.yourHandList}>
          {playerHand.map((card, index) => (
            <div key={`${card.suit}-${card.rank}`} role="listitem">
              <Card
                card={card}
                interactive
                selected={selectedCard?.suit === card.suit && selectedCard?.rank === card.rank}
                onClick={() => onCardSelect(card)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                cardRef={(el) => { cardRefs.current[index] = el; }}
                tabIndex={focusedCardIndex === -1 ? (index === 0 ? 0 : -1) : (focusedCardIndex === index ? 0 : -1)}
                ariaLabel={`${card.rank} of ${card.suit}, card ${index + 1} of ${playerHand.length}`}
              />
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className={styles.actionRow}>
          {!isPlayerAttacking && (
            <button
              className={styles.btnTake}
              onClick={onTake}
              aria-label={labels.aria.takeCards}
            >
              {labels.take}
            </button>
          )}
          {isPlayerAttacking && playZone.length > 0 && (
            <button
              className={styles.btnPass}
              onClick={onPass}
              aria-label={labels.aria.passTurn}
            >
              {labels.pass}
            </button>
          )}
          <button
            className={styles.btnHistory}
            onClick={onTracker}
            aria-label={labels.aria.tracker}
          >
            {labels.tracker}
          </button>
        </div>
      </div>
    </section>
  );
};

export default GameScreen;
